import {
  deriveRequiredSpecimens,
  freeTextCode,
  MAX_COLLECTION_SKEW_MS,
  MAX_TESTS_PER_ORDER,
  type SpecimenType,
} from '@synapse/lab-tests';
import type {
  CollectedSpecimenInput,
  CreateLabOrderInput,
  LabOrderPriority,
  LabTestDefinitionDTO,
  OrderingClinicianInput,
  OrderedTestInput,
} from '@/types/lab';

/**
 * The state and rules behind the new-order flow, with no React in sight.
 *
 * The flow's one real idea is that **the operator picks tests and the specimens
 * follow**. Nobody at the desk should have to work out that an FBC and an HbA1c
 * are one EDTA tube while a UFR is a separate pot — that is what
 * `deriveRequiredSpecimens` is for, and it is why there is no "add a specimen"
 * control anywhere in this file. Deselect the last blood test and the blood card
 * disappears on its own, so a specimen for a test nobody ordered is
 * unrepresentable rather than validated against.
 *
 * The two limits come from `@synapse/lab-tests` and not from a number typed
 * here: the server enforces the same two, and a form that lets you fill in
 * something the API will refuse is worse than one that stops you early.
 */

/** A test the operator has chosen, whether from the catalogue or typed by hand. */
export interface SelectedTest {
  /** Catalogue code, or `free:<slug>` for a typed one. Unique within a draft. */
  code: string;
  name: string;
  specimenType: SpecimenType;
  container?: string;
  freeText: boolean;
}

/** One collection card. Present only while some selected test needs this type. */
export interface SpecimenDraft {
  /** Off means "not drawn yet" — a legal order that starts awaiting collection. */
  collect: boolean;
  /** `datetime-local` value, i.e. wall-clock in the operator's own timezone. */
  collectedAt: string;
  container: string;
  labelNote: string;
}

export interface NewOrderDraft {
  patientId: string | null;
  tests: SelectedTest[];
  specimens: Record<string, SpecimenDraft>;
  ordering: OrderingClinicianInput;
  priority: LabOrderPriority;
  /** Optional by decision: an order is legal before anyone has routed it. */
  laboratoryId: string | null;
  indication: string;
}

export const NEW_ORDER_STEPS = ['patient', 'tests', 'collection'] as const;
export type NewOrderStep = (typeof NEW_ORDER_STEPS)[number];

// ---------------------------------------------------------------------------
// Local wall-clock <-> instant
// ---------------------------------------------------------------------------

/**
 * Format a Date as an `<input type="datetime-local">` value.
 *
 * Local parts on purpose, not `toISOString().slice(0,16)` — that renders UTC,
 * so in Asia/Colombo a draw taken now would display as five and a half hours
 * ago and, worse, would round-trip back out as that.
 */
export function toLocalInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Parse a `datetime-local` value as local wall-clock. `null` if unparseable. */
export function fromLocalInputValue(value: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const [, y, mo, d, h, mi] = m;
  const date = new Date(
    Number(y),
    Number(mo) - 1,
    Number(d),
    Number(h),
    Number(mi),
  );
  // Rejects 2026-02-31 and friends, which the Date constructor silently rolls
  // over into March rather than refusing.
  return Number.isNaN(date.getTime()) || date.getMonth() !== Number(mo) - 1 ? null : date;
}

// ---------------------------------------------------------------------------
// Draft construction
// ---------------------------------------------------------------------------

export function emptyDraft(currentUserId: string | null): NewOrderDraft {
  return {
    patientId: null,
    tests: [],
    // Empty until tests are chosen — the cards are derived, never added.
    specimens: {},
    // Whoever is at the desk is the likeliest orderer and is always available
    // without a members lookup, which a lab tech may not be allowed to make.
    ordering: currentUserId
      ? { kind: 'member', userId: currentUserId }
      : { kind: 'external', label: '' },
    priority: 'routine',
    laboratoryId: null,
    indication: '',
  };
}

export function definitionToSelected(def: LabTestDefinitionDTO): SelectedTest {
  return {
    code: def.code,
    name: def.name,
    specimenType: def.specimenType,
    ...(def.container ? { container: def.container } : {}),
    freeText: false,
  };
}

/** Add or remove a catalogue test. Selection is keyed by code, so it de-dupes. */
export function toggleTest(tests: SelectedTest[], def: LabTestDefinitionDTO): SelectedTest[] {
  return tests.some((t) => t.code === def.code)
    ? tests.filter((t) => t.code !== def.code)
    : [...tests, definitionToSelected(def)];
}

export type FreeTextRejection = 'empty' | 'duplicate' | 'full';

/**
 * Add a typed test.
 *
 * The `free:` slug is what the server's per-order unique index is written over,
 * so two spellings that slug the same ("Vit D" twice) are the same test and the
 * second is refused here rather than at the API as an opaque conflict.
 */
export function addFreeTextTest(
  tests: SelectedTest[],
  name: string,
  specimenType: SpecimenType,
): { tests: SelectedTest[]; rejection: FreeTextRejection | null } {
  const trimmed = name.trim();
  if (!trimmed) return { tests, rejection: 'empty' };
  if (tests.length >= MAX_TESTS_PER_ORDER) return { tests, rejection: 'full' };

  const code = freeTextCode(trimmed);
  if (tests.some((t) => t.code === code)) return { tests, rejection: 'duplicate' };

  return {
    tests: [...tests, { code, name: trimmed, specimenType, freeText: true }],
    rejection: null,
  };
}

/**
 * The specimen cards for a selection, and the drafts to back them.
 *
 * Existing drafts are carried over by type so that toggling an unrelated test
 * does not wipe a collection time the operator already corrected. A type that
 * has dropped out of the selection is dropped from the map — otherwise
 * deselecting the last urine test and re-selecting one would resurrect a
 * half-filled card the operator thought was gone.
 */
export function reconcileSpecimens(
  tests: SelectedTest[],
  existing: Record<string, SpecimenDraft>,
  now: Date = new Date(),
): Record<string, SpecimenDraft> {
  const required = deriveRequiredSpecimens(tests);
  const next: Record<string, SpecimenDraft> = {};
  for (const { specimenType, container } of required) {
    const prev = existing[specimenType];
    next[specimenType] = prev ?? {
      // Default ON: the overwhelmingly common case is that the sample is drawn
      // at the same desk, in the same minute, as the order is raised.
      collect: true,
      collectedAt: toLocalInputValue(now),
      container: container ?? '',
      labelNote: '',
    };
  }
  return next;
}

// ---------------------------------------------------------------------------
// Validation
// ---------------------------------------------------------------------------

/**
 * Why a step cannot be left, as an i18n key — or `null` when it can.
 *
 * Returning a key rather than a sentence keeps this file free of a translator
 * and lets the page decide where to render it.
 */
export function validateStep(
  step: NewOrderStep,
  draft: NewOrderDraft,
  now: Date = new Date(),
): string | null {
  if (step === 'patient') {
    return draft.patientId ? null : 'lab.new.errors.noPatient';
  }

  if (step === 'tests') {
    if (draft.tests.length === 0) return 'lab.new.errors.noTests';
    if (draft.tests.length > MAX_TESTS_PER_ORDER) return 'lab.new.errors.tooManyTests';
    return null;
  }

  if (draft.ordering.kind === 'external' && draft.ordering.label.trim() === '') {
    return 'lab.new.errors.noOrderer';
  }

  for (const spec of Object.values(draft.specimens)) {
    if (!spec.collect) continue;
    const at = fromLocalInputValue(spec.collectedAt);
    if (!at) return 'lab.new.errors.badTime';
    if (at.getTime() > now.getTime() + MAX_COLLECTION_SKEW_MS) {
      return 'lab.new.errors.futureTime';
    }
    // A mistyped year sorts the worklist into the far past as surely as a
    // future one sorts it into the future.
    if (at.getFullYear() < 1900) return 'lab.new.errors.badTime';
  }

  return null;
}

/**
 * The first step that does not validate, or `null` when the draft can be sent.
 *
 * Returns the STEP as well as the message so submit can put the operator in
 * front of the field it is complaining about. Checking only the step currently
 * on screen would let a draft through whose earlier step went invalid after the
 * fact — the patient cleared, say — and `buildCreateInput` would then throw a
 * raw English `Error` that has no translation and means nothing to a user.
 */
export function firstInvalidStep(
  draft: NewOrderDraft,
  now: Date = new Date(),
): { step: NewOrderStep; error: string } | null {
  for (const step of NEW_ORDER_STEPS) {
    const error = validateStep(step, draft, now);
    if (error) return { step, error };
  }
  return null;
}

// ---------------------------------------------------------------------------
// Submission
// ---------------------------------------------------------------------------

/**
 * Turn a validated draft into the API's create payload.
 *
 * Only specimens whose card is ticked are sent. An order with none is legal and
 * lands as `awaiting_collection` — the patient who is told to come back fasting
 * tomorrow still needs their requisition today.
 */
export function buildCreateInput(draft: NewOrderDraft): CreateLabOrderInput {
  if (!draft.patientId) {
    throw new Error('buildCreateInput called without a patient — validate the draft first');
  }

  const tests: OrderedTestInput[] = draft.tests.map((t) =>
    t.freeText
      ? { kind: 'free_text', name: t.name, specimenType: t.specimenType }
      : { kind: 'catalogue', code: t.code },
  );

  const specimens: CollectedSpecimenInput[] = Object.entries(draft.specimens)
    .filter(([, s]) => s.collect)
    .map(([type, s]) => {
      const at = fromLocalInputValue(s.collectedAt);
      if (!at) throw new Error(`unparseable collection time for ${type}`);
      const container = s.container.trim();
      const labelNote = s.labelNote.trim();
      return {
        specimenType: type as SpecimenType,
        collectedAt: at.toISOString(),
        ...(container ? { container } : {}),
        ...(labelNote ? { labelNote } : {}),
      };
    });

  const indication = draft.indication.trim();

  return {
    patientId: draft.patientId,
    tests,
    orderingClinician:
      draft.ordering.kind === 'external'
        ? { kind: 'external', label: draft.ordering.label.trim() }
        : draft.ordering,
    priority: draft.priority,
    specimens,
    // Omitted rather than sent as null: `createOrderInput` takes an optional
    // laboratory, not a nullable one — there is nothing to clear on a new order.
    ...(draft.laboratoryId ? { laboratoryId: draft.laboratoryId } : {}),
    ...(indication ? { indication } : {}),
  };
}

/** A one-line summary of what will be drawn, for the tests step. */
export function summariseSpecimens(tests: SelectedTest[]): {
  specimenType: SpecimenType;
  container: string | undefined;
  testCount: number;
}[] {
  return deriveRequiredSpecimens(tests).map(({ specimenType, container }) => ({
    specimenType,
    container,
    testCount: tests.filter((t) => t.specimenType === specimenType).length,
  }));
}
